import React from 'react';

// ============================================
// CARD COMPONENT
// ============================================
// A flexible card component for content containers.
// Uses the design system tokens from tailwind.config.js

export type CardVariant = 'default' | 'outlined' | 'elevated';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  variant?: CardVariant;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  hover?: boolean;
  children: React.ReactNode;
}

interface CardHeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface CardBodyProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

interface CardFooterProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

// Variant styles
const variantStyles: Record<CardVariant, string> = {
  default: 'bg-surface border border-neutral-200',
  outlined: 'bg-surface border-2 border-neutral-300',
  elevated: 'bg-surface border border-neutral-100 shadow-md',
};

// Padding styles
const paddingStyles: Record<string, string> = {
  none: '',
  sm: 'p-3',
  md: 'p-4',
  lg: 'p-6',
};

// Hover styles
const hoverStyles = 'hover:shadow-lg hover:border-primary-300 transition-all duration-fast cursor-pointer';

// Base styles
const baseStyles = 'rounded-lg overflow-hidden';

const Card = React.forwardRef<HTMLDivElement, CardProps>(
  (
    {
      variant = 'default',
      padding = 'none',
      hover = false,
      className = '',
      children,
      ...props
    },
    ref
  ) => {
    const cardClasses = [
      baseStyles,
      variantStyles[variant],
      paddingStyles[padding],
      hover ? hoverStyles : '',
      className,
    ]
      .join(' ')
      .replace(/\s+/g, ' ')
      .trim();

    return (
      <div ref={ref} className={cardClasses} {...props}>
        {children}
      </div>
    );
  }
);

Card.displayName = 'Card';

// Card Header component
const CardHeader: React.FC<CardHeaderProps> = ({
  className = '',
  children,
  ...props
}) => {
  return (
    <div
      className={`px-4 py-3 border-b border-neutral-100 ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
};

CardHeader.displayName = 'CardHeader';

// Card Body component
const CardBody: React.FC<CardBodyProps> = ({
  className = '',
  children,
  ...props
}) => {
  return (
    <div className={`p-4 ${className}`.trim()} {...props}>
      {children}
    </div>
  );
};

CardBody.displayName = 'CardBody';

// Card Footer component
const CardFooter: React.FC<CardFooterProps> = ({
  className = '',
  children,
  ...props
}) => {
  return (
    <div
      className={`px-4 py-3 border-t border-neutral-100 bg-neutral-50 ${className}`.trim()}
      {...props}
    >
      {children}
    </div>
  );
};

CardFooter.displayName = 'CardFooter';

export default Card;
export { CardHeader, CardBody, CardFooter };
